// client/src/pages/PTO/PTODashboard.tsx
// Main orchestrator for the PTO Dashboard page.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CalendarDays } from 'lucide-react';

import type {
  PTOEmployee,
  PTOEntry,
  PTOHoliday,
  PTOEntryInput,
  FrontDeskAssignment,
  FrontDeskDefault,
  PhoneDutyAssignment,
  AdminDutyAssignment,
} from './ptoTypes';
import { mondayOf, weekDatesFor } from './ptoUtils';
import {
  fetchPTOBootstrap,
  createEntry,
  updateEntry,
  deleteEntry,
  upsertPhoneDuty,
  deletePhoneDuty,
  upsertFrontDesk,
  deleteFrontDesk,
} from '../../services/pto';

import BinaryRainCanvas from '../../components/BinaryRainCanvas';
import StatsBar from './StatsBar';
import WhosOutPanel from './WhosOutPanel';
import FlexFridaysPanel from './FlexFridaysPanel';
import FrontDeskCoverage from './FrontDeskCoverage';
import PhoneCoverage from './PhoneCoverage';
import AdminCoveragePanel from './AdminCoveragePanel';
import PTOHeatMap from './PTOHeatMap';
import PTODataTable from './PTODataTable';
import PTOEntryModal from './PTOEntryModal';
import IndividualReportModal from './IndividualReportModal';
import ExportModal from './ExportModal';

// ─── Helpers ────────────────────────────────────────────────────────────────

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Entry types that count as "out" for the stats bar. */
const OUT_TYPES = new Set(['PTO', 'Holiday', 'Sabbatical', 'Half Day', 'Flexing Off']);

// ─── Component ──────────────────────────────────────────────────────────────

export default function PTODashboard() {
  // Core data
  const [employees, setEmployees] = useState<PTOEmployee[]>([]);
  const [entries, setEntries] = useState<PTOEntry[]>([]);
  const [holidays, setHolidays] = useState<PTOHoliday[]>([]);
  const [frontDesk, setFrontDesk] = useState<FrontDeskAssignment[]>([]);
  const [frontDeskDefaults, setFrontDeskDefaults] = useState<FrontDeskDefault[]>([]);
  const [phoneDuty, setPhoneDuty] = useState<PhoneDutyAssignment[]>([]);
  const [adminDuty, setAdminDuty] = useState<AdminDutyAssignment[]>([]);
  const [canEditCoverage, setCanEditCoverage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [currentWeek, setCurrentWeek] = useState<Date>(() => mondayOf(new Date()));
  const [editingEntry, setEditingEntry] = useState<PTOEntry | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [prefillEntry, setPrefillEntry] = useState<{
    staffIndex?: string;
    entryType?: PTOEntryInput['entryType'];
    entryDate?: string;
  } | null>(null);
  const [reportEmployee, setReportEmployee] = useState<PTOEmployee | null>(null);
  const [showExport, setShowExport] = useState(false);

  // ── Bootstrap ───────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPTOBootstrap();
      setEmployees(data.employees);
      setEntries(data.entries);
      setHolidays(data.holidays);
      setFrontDesk(data.frontDesk);
      setFrontDeskDefaults(data.frontDeskDefaults);
      setPhoneDuty(data.phoneDuty);
      setAdminDuty(data.adminDuty || []);
      setCanEditCoverage(data.canEditCoverage === true);
    } catch (err: any) {
      setError(err.message || 'Failed to load PTO data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Derived data ────────────────────────────────────────────────────────

  const today = todayIso();

  const employeeMap = useMemo(() => {
    const map = new Map<string, PTOEmployee>();
    for (const emp of employees) map.set(emp.staffIndex, emp);
    return map;
  }, [employees]);

  // Filter out entries for employees not in the roster (e.g. departed staff with stale PE data)
  const knownEntries = useMemo(
    () => entries.filter((e) => employeeMap.has(e.staffIndex)),
    [entries, employeeMap],
  );

  const todayOutCount = useMemo(
    () => knownEntries.filter((e) => e.entryDate === today && OUT_TYPES.has(e.entryType)).length,
    [knownEntries, today],
  );

  const pendingCount = useMemo(
    () => knownEntries.filter((e) => e.entryType === 'Tentative PTO').length,
    [knownEntries],
  );

  const weekDates = useMemo(() => weekDatesFor(currentWeek), [currentWeek]);

  const weekEntries = useMemo(() => {
    const set = new Set(weekDates);
    return knownEntries.filter((e) => set.has(e.entryDate));
  }, [knownEntries, weekDates]);

  // ── CRUD handlers ─────────────────────────────────────────────────────

  // Note: these helpers no longer toast — the calling flow (handleSaveEntry) fires
  // a single consolidated toast so multi-day Sabbaticals don't spam 30+ notifications.
  const handleCreateEntry = useCallback(async (input: PTOEntryInput) => {
    const { id } = await createEntry(input);
    const newEntry: PTOEntry = {
      id,
      staffIndex: input.staffIndex,
      entryType: input.entryType,
      entryDate: input.entryDate,
      hours: input.hours ?? null,
      startTime: input.startTime ?? null,
      endTime: input.endTime ?? null,
      note: input.note ?? null,
      isApproved: false,
      source: 'manual',
      swapPartner: input.swapPartner ?? null,
      createdBy: '',
    };
    setEntries((prev) => [...prev, newEntry]);
  }, []);

  const handleUpdateEntry = useCallback(async (id: string, input: Partial<PTOEntryInput>) => {
    await updateEntry(id, input);
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, ...input } : e)),
    );
  }, []);

  const handleDeleteEntry = useCallback(async (id: string) => {
    await deleteEntry(id);
    setEntries((prev) => prev.filter((e) => e.id !== id));
    toast.success('Entry deleted');
  }, []);

  const handleBulkDelete = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    // Fire all delete calls concurrently; allSettled so one failure doesn't
    // hide the others' results.
    const results = await Promise.allSettled(ids.map((id) => deleteEntry(id)));
    const succeededIds = ids.filter((_, i) => results[i].status === 'fulfilled');
    const failedCount = results.length - succeededIds.length;

    if (succeededIds.length > 0) {
      const idSet = new Set(succeededIds);
      setEntries((prev) => prev.filter((e) => !idSet.has(e.id)));
    }

    if (failedCount === 0) {
      toast.success(
        succeededIds.length === 1 ? 'Entry deleted' : `Deleted ${succeededIds.length} entries`,
      );
    } else if (succeededIds.length === 0) {
      toast.error(`Failed to delete ${failedCount} ${failedCount === 1 ? 'entry' : 'entries'}.`);
    } else {
      toast.error(`Deleted ${succeededIds.length}; ${failedCount} failed.`);
    }
  }, []);

  // ── Coverage CRUD (admin-only) ──────────────────────────────────────

  const handleSavePhoneDuty = useCallback(async (payload: {
    dutyDate: string;
    staffIndex: string;
    timeIn: string | null;
    timeOut: string | null;
  }) => {
    const { id } = await upsertPhoneDuty(payload);
    setPhoneDuty((prev) => {
      const idx = prev.findIndex(
        (p) => p.dutyDate === payload.dutyDate && p.staffIndex === payload.staffIndex,
      );
      const next: PhoneDutyAssignment = {
        id,
        dutyDate: payload.dutyDate,
        staffIndex: payload.staffIndex,
        timeIn: payload.timeIn,
        timeOut: payload.timeOut,
      };
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = next;
        return copy;
      }
      return [...prev, next];
    });
  }, []);

  const handleDeletePhoneDuty = useCallback(async (id: number) => {
    await deletePhoneDuty(id);
    setPhoneDuty((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const handleSaveFrontDesk = useCallback(async (payload: {
    id?: number;
    office: string;
    coverageDate: string;
    staffIndex: string;
    timeIn: string | null;
    timeOut: string | null;
    note: string | null;
  }) => {
    const { id } = await upsertFrontDesk(payload);
    setFrontDesk((prev) => {
      const next: FrontDeskAssignment = {
        id,
        office: payload.office,
        coverageDate: payload.coverageDate,
        staffIndex: payload.staffIndex,
        timeIn: payload.timeIn,
        timeOut: payload.timeOut,
        note: payload.note,
        isNoCoverage: false,
      };
      // Edit targets a specific row by id; add appends a new one. The same
      // person can have multiple rows on a day, so never merge by staffIndex.
      const idx = prev.findIndex((f) => f.id === id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = next;
        return copy;
      }
      return [...prev, next];
    });
  }, []);

  const handleDeleteFrontDesk = useCallback(async (id: number) => {
    await deleteFrontDesk(id);
    setFrontDesk((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleWeekChange = useCallback((direction: -1 | 1) => {
    setCurrentWeek((prev) => {
      const next = new Date(prev);
      next.setDate(prev.getDate() + direction * 7);
      return next;
    });
  }, []);

  const handleJumpToWeek = useCallback((monday: Date) => {
    setCurrentWeek(monday);
  }, []);

  const handleEmployeeClick = useCallback((emp: PTOEmployee) => {
    setReportEmployee(emp);
  }, []);

  const handleSaveEntry = useCallback(async (inputs: PTOEntryInput[]) => {
    if (editingEntry) {
      await handleUpdateEntry(editingEntry.id, inputs[0]);
      toast.success('Entry updated');
    } else {
      for (const input of inputs) {
        // eslint-disable-next-line no-await-in-loop -- sequential keeps optimistic state consistent
        await handleCreateEntry(input);
      }
      toast.success(
        inputs.length === 1 ? 'Entry created' : `Created ${inputs.length} entries`,
      );
    }
    setShowNewModal(false);
    setEditingEntry(null);
    setPrefillEntry(null);
  }, [editingEntry, handleCreateEntry, handleUpdateEntry]);

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Page header */}
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarDays size={20} style={{ color: 'var(--accent)' }} />
          PTO Dashboard
        </div>
        <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
          Admin team schedule, coverage, and time-off tracking
        </div>
      </div>

      {loading ? (
        <div
          style={{
            position: 'relative',
            borderRadius: 18,
            overflow: 'hidden',
            minHeight: 320,
            background: 'linear-gradient(135deg, rgba(10,12,16,1) 0%, rgba(14,26,42,1) 50%, rgba(10,12,16,1) 100%)',
            border: '1px solid rgba(122,162,255,0.16)',
            boxShadow: '0 0 80px rgba(122,162,255,0.06), 0 20px 60px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 20,
          }}
        >
          <BinaryRainCanvas opacity={0.18} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 18,
                background: 'rgba(122,162,255,0.07)',
                border: '1px solid rgba(122,162,255,0.28)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 40px rgba(122,162,255,0.22), 0 0 80px rgba(100,210,255,0.08)',
                animation: 'logoPulse 4s ease-in-out infinite',
              }}
            >
              <CalendarDays size={30} style={{ color: 'var(--accent)' }} />
            </div>
          </div>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'baseline', gap: 2 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'rgba(167,179,196,0.9)', letterSpacing: '0.3px' }}>
              Loading
            </span>
            <span style={{ display: 'inline-flex', gap: 3, marginLeft: 2 }}>
              {[0, 1, 2].map(i => (
                <span
                  key={i}
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: 'var(--accent)',
                    display: 'inline-block',
                    animation: `typingDot 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </span>
          </div>
        </div>
      ) : error ? (
        <div style={{ color: 'var(--bad)' }}>{error}</div>
      ) : (
        <>
          <StatsBar
            todayOutCount={todayOutCount}
            pendingCount={pendingCount}
            onNewEntry={() => setShowNewModal(true)}
            onExport={() => setShowExport(true)}
            onRefresh={loadData}
          />

          {/* Who's Out */}
          <div className="card">
            <WhosOutPanel
              entries={knownEntries}
              employees={employees}
              holidays={holidays}
              currentWeek={currentWeek}
              onWeekChange={handleWeekChange}
              onJumpTo={handleJumpToWeek}
              onEmployeeClick={handleEmployeeClick}
            />
          </div>

          {/* Flex Fridays + Coverage side by side on wide screens */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 14 }}>
            <div className="card">
              <div className="hd" style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                Flex Fridays
              </div>
              <div className="bd">
                <FlexFridaysPanel
                  entries={knownEntries}
                  employees={employees}
                  currentWeek={currentWeek}
                  canEdit={canEditCoverage}
                  onAddFridayWorker={(fridayDate) =>
                    setPrefillEntry({ entryType: 'Friday Work', entryDate: fridayDate })
                  }
                  onEditEntry={setEditingEntry}
                  onDeleteEntry={handleDeleteEntry}
                />
              </div>
            </div>

            <div className="card">
              <div className="hd" style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                Phone Coverage
              </div>
              <div className="bd">
                <PhoneCoverage
                  phoneDuty={phoneDuty}
                  employees={employees}
                  currentWeek={currentWeek}
                  canEdit={canEditCoverage}
                  onSavePhoneDuty={handleSavePhoneDuty}
                  onDeletePhoneDuty={handleDeletePhoneDuty}
                />
              </div>
            </div>
          </div>

          {/* Admin Coverage — weekly email / fax / SOW duty */}
          <div className="card">
            <div className="hd" style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>
              Admin Coverage
            </div>
            <div className="bd">
              <AdminCoveragePanel
                adminDuty={adminDuty}
                employees={employees}
                currentWeek={currentWeek}
              />
            </div>
          </div>

          {/* Front Desk Coverage */}
          <div className="card">
            <div className="hd" style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>
              Front Desk Coverage
            </div>
            <div className="bd">
              <FrontDeskCoverage
                frontDesk={frontDesk}
                frontDeskDefaults={frontDeskDefaults}
                employees={employees}
                currentWeek={currentWeek}
                canEdit={canEditCoverage}
                onSaveFrontDesk={handleSaveFrontDesk}
                onDeleteFrontDesk={handleDeleteFrontDesk}
              />
            </div>
          </div>

          {/* PTO Heat Map */}
          <div className="card">
            <PTOHeatMap
              entries={knownEntries}
              employees={employees}
              onEmployeeClick={handleEmployeeClick}
            />
          </div>

          {/* Data Table */}
          <div className="card">
            <div className="hd" style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>
              All Entries
            </div>
            <div className="bd" style={{ padding: 0 }}>
              <PTODataTable
                entries={knownEntries}
                employees={employees}
                onEdit={setEditingEntry}
                onDelete={handleDeleteEntry}
                onBulkDelete={handleBulkDelete}
                onEmployeeClick={handleEmployeeClick}
              />
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {(showNewModal || editingEntry || prefillEntry) && (
        <PTOEntryModal
          entry={editingEntry}
          prefill={prefillEntry}
          employees={employees}
          existingEntries={knownEntries}
          onSave={handleSaveEntry}
          onClose={() => {
            setShowNewModal(false);
            setEditingEntry(null);
            setPrefillEntry(null);
          }}
        />
      )}

      {reportEmployee && (
        <IndividualReportModal
          employee={reportEmployee}
          entries={knownEntries}
          onClose={() => setReportEmployee(null)}
        />
      )}

      {showExport && (
        <ExportModal
          entries={knownEntries}
          employees={employees}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}
